import { useMemo, useState } from "react";
import { useMyCriticalAlerts } from "../../../../hooks/useMyCriticalAlerts";
import { useResolveWorklistAlert } from "../../../../hooks/useResolveWorklistAlert";
import UiModalAlert from "../../../../components/modal-alert/ModalAlert.component";
import { Task } from "../../../../models/entities/task.model";
import { getSessionStorageUser } from "../../../../services/session/auth.service";
import WorklistSection from "../WorklistSection";
import { useWorklist } from "../WorklistContext";
import { sortCriticalAlerts } from "../worklist.service";
import CriticalAlertRow from "./CriticalAlertRow";

export const CRITICAL_ALERTS_SECTION_ID = "critical-alerts";

/**
 * "Critical alerts" — the first section of Home's Today's worklist (KAN-2553).
 *
 * Edit reuses the Requires attention modal. Note on what a coach will actually
 * see: since July, critical-reading alerts for non-employer patients are
 * auto-assigned to a single RN rather than to the patient's own coach, and
 * Maria chose to keep that routing (KAN-2553 comment, answer #2). This section
 * shows what is assigned to YOU — so for most coaches it will be empty by design
 * until the routing changes. That is the correct behaviour for the ticket, not a
 * bug in this component.
 */
const CriticalAlertsSection = () => {
	const sessionUser = getSessionStorageUser();
	const { alerts, count, isLoading, isError, invalidate, hideAlert } = useMyCriticalAlerts(
		sessionUser?.id
	);
	// See useResolveWorklistAlert for resolve logic and optimistic-hide details.
	const { resolve, isPending } = useResolveWorklistAlert(hideAlert, sessionUser?.id);
	const { getRowOffset } = useWorklist();

	// Priority first, then newest (Maria's answer #6).
	const sortedAlerts = useMemo(() => sortCriticalAlerts(alerts), [alerts]);

	const [alertModal, setAlertModal] = useState<{ entity: Task | null; isVisible: boolean }>({
		entity: null,
		isVisible: false,
	});

	const edit = (alert: Task) => setAlertModal({ entity: alert, isVisible: true });
	const cancelAlertModal = () => setAlertModal((prev) => ({ ...prev, isVisible: false }));
	// The modal's own save invalidates the shared task query keys; invalidate()
	// additionally refreshes this section's count.
	const completeAlertModal = () => {
		setAlertModal((prev) => ({ ...prev, isVisible: false }));
		invalidate();
	};

	const rowOffset = getRowOffset(CRITICAL_ALERTS_SECTION_ID);

	return (
		<>
			<WorklistSection
				sectionId={CRITICAL_ALERTS_SECTION_ID}
				titleKey="HOME.WORKLIST.CRITICAL_ALERTS.TITLE"
				count={count}
				isLoading={isLoading}
				isError={isError}
				isEmpty={sortedAlerts.length === 0}
			>
				<ul className="worklist-rows">
					{sortedAlerts.map((alert, position) => (
						<CriticalAlertRow
							key={alert.id}
							alert={alert}
							index={rowOffset + position + 1}
							onResolve={resolve}
							onEdit={edit}
							isResolving={isPending}
						/>
					))}
				</ul>
			</WorklistSection>
			{alertModal.isVisible && alertModal.entity && (
				<UiModalAlert
					entity={alertModal.entity}
					isVisible={alertModal.isVisible}
					onCancel={cancelAlertModal}
					onComplete={completeAlertModal}
				/>
			)}
		</>
	);
};

export default CriticalAlertsSection;
